import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { instanceContext } from './context';
import { IChatProvider, IConfiguration } from '@enuan/chat__utils';
import { configurationContext } from '../configuration/context';
import { StatesProvider } from '../states/Provider';
import { fetchConfiguration } from '../configuration';
import { useDispatch } from 'react-redux';
import { messagesSlice } from 'src/reduxStore/slices/messages.slice';

export const InstanceProvider = ({
	instance,
	configuration,
	children,
	baseUrl,
}: React.PropsWithChildren<{
	instance: IChatProvider;
	configuration: IConfiguration;
	baseUrl: string;
}>) => {
	const dispatch = useDispatch();
	const [fetchedConfig, setFetchedConfig] = useState<IConfiguration | null>(
		null,
	);

	useEffect(() => {
		setFetchedConfig(null);
	}, [configuration]);

	const effectiveConfiguration = fetchedConfig ?? configuration;

	const startConnection = useCallback(
		(timestamp?: string) => {
			console.log('Starting connection');
			return instance?.initConnection(timestamp);
		},
		[instance],
	);

	const refreshCredentials = useCallback(
		() =>
			fetchConfiguration(baseUrl).then((configuration) => {
				setFetchedConfig(configuration);
				// Rebuild both transports with the page-scoped JWT while preserving
				// conversation history and reconnection state.
				instance.init(configuration);
			}),
		[baseUrl, instance],
	);

	const restart = useCallback(
		() =>
			refreshCredentials().then(() => {
				startConnection();
				dispatch(messagesSlice.actions.clearHistory());
			}),
		[dispatch, refreshCredentials, startConnection],
	);

	return (
		<instanceContext.Provider
			value={useMemo(
				() => ({ instance, restart, refreshCredentials, startConnection }),
				[instance, restart, refreshCredentials, startConnection],
			)}
		>
			<configurationContext.Provider value={effectiveConfiguration}>
				<StatesProvider>{children}</StatesProvider>
			</configurationContext.Provider>
		</instanceContext.Provider>
	);
};
